import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react'
import { scopeSchema } from './schema'
import { apiRequest } from '../../api/client'
import {
  clearAllSchemaOverrides,
  readSchemaOverridesSync,
  setSchemaOverridesEntry,
  subscribeToSchemaOverrides,
} from './schema/overridesCache'
import { useAuthStore } from '../../store/authStore'

const PdfMockupPanel = lazy(() => import('./components/PdfMockupPanel'))

export function SchemaViewerPage() {
  const [activeTab, setActiveTab] = useState<string>('all')
  const [editMode, setEditMode] = useState<boolean>(false)
  const [importError, setImportError] = useState<string>('')
  const [proposedByKey, setProposedByKey] = useState<Record<string, Record<string, string>>>({})
  const [aiLoadingByCellKey, setAiLoadingByCellKey] = useState<Record<string, boolean>>({})
  const [aiErrorByCellKey, setAiErrorByCellKey] = useState<Record<string, string>>({})
  const [aiPromptTemplatesByProp, setAiPromptTemplatesByProp] = useState<
    Record<'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary', string>
  >(() => {
    const defaults: Record<'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary', string> = {
      tooltip:
        `You are writing schema metadata for a product UI.\n\nTask: Write a TOOLTIP for the field in the context below.\n\nRules:\n- Tooltip clarifies a single term or ambiguity.\n- 1 short sentence or phrase.\n- Do not repeat the label.\n- No UI instructions.\n- Max ~12 words.\n- For consistency, use other cells in the row (see rowValues.effective) as supporting context but do not rewrite them.\n\nReturn ONLY the tooltip text for the target cell, no quotes, no JSON.\n\nContext (JSON):\n{{contextJson}}\n\nCurrent value:\n{{currentValue}}`,
      formDescription:
        `You are writing schema metadata for a product UI.\n\nTask: Write a FORM DESCRIPTION (helper text shown under/near the field) for the field in the context below.\n\nRules:\n- Explain what to enter and why.\n- 1–2 sentences.\n- Plain language.\n- Include constraints/examples if helpful.\n- No UI instructions.\n- Max ~40 words.\n- For consistency, use other cells in the row (see rowValues.effective) as supporting context but do not rewrite them.\n\nReturn ONLY the form description text for the target cell, no quotes, no JSON.\n\nContext (JSON):\n{{contextJson}}\n\nCurrent value:\n{{currentValue}}`,
      aiContext:
        `You are writing internal AI guidance for a schema field.\n\nTask: Write AI CONTEXT that helps an AI or internal system populate or reason about this field.\n\nRules:\n- Not shown to end users.\n- Be specific: definitions, policies, edge cases, guardrails.\n- Prefer concrete rules over marketing.\n- Max ~80 words.\n- For consistency, use other cells in the row (see rowValues.effective) as supporting context but do not rewrite them.\n\nReturn ONLY the AI context text for the target cell, no quotes, no JSON.\n\nContext (JSON):\n{{contextJson}}\n\nCurrent value:\n{{currentValue}}`,
      pdfDescription:
        `You are writing client-facing text that will appear in an exported PDF used for internal stakeholder decision-making.\n\nTask: Write a PDF DESCRIPTION for the item in the context below.\n\nRules:\n- Decision-forcing: the goal is to help stakeholders choose an option.\n- Include: (1) what decision is needed, (2) ramifications (risk/compliance/cost/timeline/UX), (3) 2–4 options (include a recommended default if appropriate), (4) how to decide (owner + required inputs).\n- Plain business language.\n- No UI instructions.\n- No references, citations, or links.\n- Max ~80 words.\n- For consistency, use other cells in the row (see rowValues.effective) as supporting context but do not rewrite them.\n\nReturn ONLY the PDF description text for the target cell, no quotes, no JSON.\n\nContext (JSON):\n{{contextJson}}\n\nCurrent value:\n{{currentValue}}`,
      pdfDecisionSummary:
        `You are writing a brief summary to appear in a PDF when a decision has already been made by the customer.\n\nTask: Write a PDF DECISION SUMMARY for the item in the context below.\n\nRules:\n- Shown only when the customer HAS made a decision — summarise what was decided and any key implications.\n- 1–2 sentences, plain business language.\n- Do NOT list options or ask questions — the decision is already made.\n- No references, citations, or links.\n- Max ~40 words.\n- For consistency, use other cells in the row (see rowValues.effective) as supporting context.\n\nReturn ONLY the decision summary text for the target cell, no quotes, no JSON.\n\nContext (JSON):\n{{contextJson}}\n\nCurrent value:\n{{currentValue}}`,
    }

    try {
      const raw = window.localStorage.getItem('schemaViewer:aiPromptTemplatesByProp')
      if (!raw) return defaults
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return defaults
      return {
        ...defaults,
        ...(parsed as Partial<typeof defaults>),
      }
    } catch {
      return defaults
    }
  })
  const [promptModalOpen, setPromptModalOpen] = useState<boolean>(false)
  const [promptModalProp, setPromptModalProp] = useState<'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary'>('formDescription')
  const [promptModalValue, setPromptModalValue] = useState<string>('')
  const [overridesByKey, setOverridesByKey] = useState<Record<string, Record<string, string>>>(
    () => readSchemaOverridesSync(),
  )
  const canEditSchemaOverrides = useAuthStore((s) => s.user?.permissions.canEditSchemaOverrides ?? false)
  const [decisionByCellKey, setDecisionByCellKey] = useState<Record<string, 'accepted' | 'rejected'>>({})
  const [editingCellKey, setEditingCellKey] = useState<string>('')
  const [editingValue, setEditingValue] = useState<string>('')
  const [bulkAiRunning, setBulkAiRunning] = useState(false)
  const [bulkAiProgress, setBulkAiProgress] = useState<{ done: number; total: number } | null>(null)
  const bulkAiCancelledRef = useRef(false)

  // Subscribe to server-side override cache updates (covers writes from
  // other surfaces like PdfMockupPanel so this view stays in sync).
  useEffect(() => {
    const unsubscribe = subscribeToSchemaOverrides((map) => {
      setOverridesByKey({ ...map })
    })
    return unsubscribe
  }, [])

  // Persist local edits to the server. We diff against the previous
  // overridesByKey snapshot and only PUT entities that actually changed.
  const prevOverridesRef = useRef<Record<string, Record<string, string>>>(overridesByKey)
  useEffect(() => {
    const prev = prevOverridesRef.current
    const curr = overridesByKey
    prevOverridesRef.current = curr

    if (!canEditSchemaOverrides) return // server will 403 anyway; skip the noise

    const keys = new Set<string>([...Object.keys(prev), ...Object.keys(curr)])
    for (const k of keys) {
      const before = JSON.stringify(prev[k] ?? {})
      const after = JSON.stringify(curr[k] ?? {})
      if (before === after) continue
      void setSchemaOverridesEntry(k, curr[k] ?? {})
    }
  }, [overridesByKey, canEditSchemaOverrides])

  useEffect(() => {
    try {
      window.localStorage.setItem('schemaViewer:aiPromptTemplatesByProp', JSON.stringify(aiPromptTemplatesByProp))
    } catch {
      // ignore
    }
  }, [aiPromptTemplatesByProp])

  useEffect(() => {
    if (!editMode) {
      setEditingCellKey('')
      setEditingValue('')
    }
  }, [editMode])

  const optionLabelsForField = (f: { valueType: string; options?: Array<{ label: string }> }): string[] => {
    if (f.options && f.options.length > 0) {
      return f.options.map((o) => o.label).filter(Boolean)
    }

    if (f.valueType === 'tri_state') {
      return ["Don't Know", 'Yes', 'No']
    }

    if (f.valueType === 'boolean') {
      return ['Yes', 'No']
    }

    return []
  }

  const sections = scopeSchema.sections

  const escapeCsvValue = (value: unknown): string => {
    if (value === null || value === undefined) return ''
    const str = String(value)
    if (/[\n\r",]/.test(str)) {
      return `"${str.split('"').join('""')}"`
    }
    return str
  }

  const toCsv = (rows: Array<Record<string, unknown>>, headers: string[]): string => {
    const headerLine = headers.map((h) => escapeCsvValue(h)).join(',')
    const lines = rows.map((r) => headers.map((h) => escapeCsvValue(r[h])).join(','))
    return [headerLine, ...lines].join('\n')
  }

  const parseCsv = (csvText: string): { headers: string[]; rows: Array<Record<string, string>> } => {
    const text = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const lines = text.split('\n').filter((l) => l.trim().length > 0)
    if (lines.length === 0) return { headers: [], rows: [] }

    const parseLine = (line: string): string[] => {
      const out: string[] = []
      let cur = ''
      let i = 0
      let inQuotes = false
      while (i < line.length) {
        const ch = line[i]
        if (inQuotes) {
          if (ch === '"') {
            const next = line[i + 1]
            if (next === '"') {
              cur += '"'
              i += 2
              continue
            }
            inQuotes = false
            i += 1
            continue
          }
          cur += ch
          i += 1
          continue
        }

        if (ch === ',') {
          out.push(cur)
          cur = ''
          i += 1
          continue
        }
        if (ch === '"') {
          inQuotes = true
          i += 1
          continue
        }
        cur += ch
        i += 1
      }
      out.push(cur)
      return out
    }

    const headers = parseLine(lines[0]).map((h) => h.trim())
    const rows = lines.slice(1).map((line) => {
      const values = parseLine(line)
      const row: Record<string, string> = {}
      for (let i = 0; i < headers.length; i += 1) {
        row[headers[i]] = values[i] ?? ''
      }
      return row
    })
    return { headers, rows }
  }

  const visibleSections = useMemo(() => {
    if (activeTab === 'all') return sections
    return sections.filter((s) => s.tabId === activeTab)
  }, [activeTab, sections])

  const groups = useMemo(() => visibleSections.flatMap((s) => s.groups || []), [visibleSections])
  const fields = useMemo(() => visibleSections.flatMap((s) => s.fields), [visibleSections])

  const sectionById = useMemo(() => new Map(visibleSections.map((s) => [s.id, s])), [visibleSections])
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups])
  const fieldById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields])

  const keyFor = (rowType: 'section' | 'group' | 'field', id: string) => `${rowType}:${id}`
  const cellKeyFor = (rowType: 'section' | 'group' | 'field', id: string, prop: string) =>
    `${rowType}:${id}:${prop}`

  const getOverride = (rowType: 'section' | 'group' | 'field', id: string, prop: string) => {
    const key = keyFor(rowType, id)
    return overridesByKey[key]?.[prop]
  }

  const getProposed = (rowType: 'section' | 'group' | 'field', id: string, prop: string) => {
    const key = keyFor(rowType, id)
    return proposedByKey[key]?.[prop]
  }

  const getEffectiveValue = (
    rowType: 'section' | 'group' | 'field',
    id: string,
    prop: 'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary',
    baseValue: string
  ) => {
    const override = getOverride(rowType, id, prop)
    return override !== undefined ? override : baseValue
  }

  const isFilled = (v: unknown) => typeof v === 'string' && v.trim().length > 0

  const toPct = (filled: number, total: number) => {
    if (total <= 0) return 100
    return Math.round((filled / total) * 100)
  }

  const acceptCell = (rowType: 'section' | 'group' | 'field', id: string, prop: string) => {
    const key = keyFor(rowType, id)
    const proposed = proposedByKey[key]?.[prop]
    if (proposed === undefined) return

    setDecisionByCellKey((prev) => ({
      ...prev,
      [cellKeyFor(rowType, id, prop)]: 'accepted',
    }))

    setOverridesByKey((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [prop]: proposed,
      },
    }))

    setProposedByKey((prev) => {
      const next = { ...prev }
      const row = { ...(next[key] || {}) }
      delete row[prop]
      if (Object.keys(row).length === 0) {
        delete next[key]
      } else {
        next[key] = row
      }
      return next
    })
  }

  const buildPromptFromTemplate = (template: string, context: Record<string, unknown>, currentValue: string) => {
    const contextJson = JSON.stringify(context, null, 2)
    return template
      .split('{{contextJson}}')
      .join(contextJson)
      .split('{{currentValue}}')
      .join(currentValue || '')
  }

  const askAiForCell = async (args: {
    rowType: 'section' | 'group' | 'field'
    id: string
    prop: 'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary'
    currentValue: string
  }) => {
    if (!editMode) return
    const cellKey = cellKeyFor(args.rowType, args.id, args.prop)

    setAiLoadingByCellKey((prev) => ({ ...prev, [cellKey]: true }))
    setAiErrorByCellKey((prev) => {
      const next = { ...prev }
      delete next[cellKey]
      return next
    })

    try {
      const section = args.rowType === 'section' ? sectionById.get(args.id) : undefined
      const group = args.rowType === 'group' ? groupById.get(args.id) : undefined
      const field = args.rowType === 'field' ? fieldById.get(args.id) : undefined

      const baseRowValues = {
        formDescription: (section as any)?.formDescription || (group as any)?.formDescription || (field as any)?.formDescription || '',
        tooltip: (section as any)?.tooltip || (group as any)?.tooltip || (field as any)?.tooltip || '',
        aiContext: (section as any)?.aiContext || (group as any)?.aiContext || (field as any)?.aiContext || '',
        pdfDescription: (section as any)?.pdfDescription || (group as any)?.pdfDescription || (field as any)?.pdfDescription || '',
        pdfDecisionSummary: (section as any)?.pdfDecisionSummary || (group as any)?.pdfDecisionSummary || (field as any)?.pdfDecisionSummary || '',
      }

      const effectiveRowValues = {
        formDescription: getEffectiveValue(args.rowType, args.id, 'formDescription', baseRowValues.formDescription),
        tooltip: getEffectiveValue(args.rowType, args.id, 'tooltip', baseRowValues.tooltip),
        aiContext: getEffectiveValue(args.rowType, args.id, 'aiContext', baseRowValues.aiContext),
        pdfDescription: getEffectiveValue(args.rowType, args.id, 'pdfDescription', baseRowValues.pdfDescription),
        pdfDecisionSummary: getEffectiveValue(args.rowType, args.id, 'pdfDecisionSummary', baseRowValues.pdfDecisionSummary),
      }

      const context: Record<string, unknown> = {
        tabId: activeTab,
        rowType: args.rowType,
        id: args.id,
        prop: args.prop,
        targetProp: args.prop,
        formTitle: (section as any)?.formTitle || '',
        formLabel: (field as any)?.formLabel || (group as any)?.formLabel || (section as any)?.formLabel || '',
        pdfTitle: (section as any)?.pdfTitle || '',
        pdfLabel: (field as any)?.pdfLabel || (group as any)?.pdfLabel || (section as any)?.pdfLabel || '',
        sectionId: (group as any)?.sectionId || (field as any)?.sectionId || (section as any)?.id || '',
        groupId: (field as any)?.groupId || (group as any)?.id || '',
        path: (field as any)?.path || (group as any)?.path || (section as any)?.path || '',
        valueType: (field as any)?.valueType || '',
        options: Array.isArray((field as any)?.options)
          ? (field as any).options.map((o: any) => ({ label: o?.label, value: o?.value }))
          : [],
        visibleWhen: (field as any)?.visibleWhen || (group as any)?.visibleWhen || (section as any)?.visibleWhen || null,
        rowValues: {
          base: baseRowValues,
          effective: effectiveRowValues,
        },
      }

      const prompt = buildPromptFromTemplate(aiPromptTemplatesByProp[args.prop], context, args.currentValue || '')

      const res = await apiRequest<{ suggestion: string }>('/schema-ai/suggest', {
        method: 'POST',
        body: JSON.stringify({
          prop: args.prop,
          prompt,
          currentValue: args.currentValue || '',
          context,
        }),
      })

      if (!res.success) {
        setAiErrorByCellKey((prev) => ({ ...prev, [cellKey]: res.error || 'AI request failed' }))
        return
      }

      const suggestion = (res.data as any)?.suggestion
      if (typeof suggestion !== 'string' || suggestion.trim().length === 0) {
        setAiErrorByCellKey((prev) => ({ ...prev, [cellKey]: 'AI returned no suggestion' }))
        return
      }

      const rowKey = keyFor(args.rowType, args.id)
      setProposedByKey((prev) => ({
        ...prev,
        [rowKey]: {
          ...(prev[rowKey] || {}),
          [args.prop]: suggestion.trim(),
        },
      }))

      setDecisionByCellKey((prev) => {
        const next = { ...prev }
        delete next[cellKey]
        return next
      })
    } catch (e) {
      setAiErrorByCellKey((prev) => ({
        ...prev,
        [cellKey]: e instanceof Error ? e.message : 'AI request failed',
      }))
    } finally {
      setAiLoadingByCellKey((prev) => ({ ...prev, [cellKey]: false }))
    }
  }

  const acceptAllProposals = () => {
    const proposals = proposedByKey
    if (Object.keys(proposals).length === 0) return

    setOverridesByKey((prev) => {
      const next = { ...prev }
      for (const [rowKey, props] of Object.entries(proposals)) {
        next[rowKey] = {
          ...(next[rowKey] || {}),
          ...props,
        }
      }
      return next
    })

    setDecisionByCellKey((prev) => {
      const next = { ...prev }
      for (const [rowKey, props] of Object.entries(proposals)) {
        const [rowType, id] = rowKey.split(':')
        if (!rowType || !id) continue
        for (const prop of Object.keys(props)) {
          next[cellKeyFor(rowType as any, id, prop)] = 'accepted'
        }
      }
      return next
    })

    setProposedByKey({})
  }

  const rejectAllProposals = () => {
    const proposals = proposedByKey
    if (Object.keys(proposals).length === 0) return

    setDecisionByCellKey((prev) => {
      const next = { ...prev }
      for (const [rowKey, props] of Object.entries(proposals)) {
        const [rowType, id] = rowKey.split(':')
        if (!rowType || !id) continue
        for (const prop of Object.keys(props)) {
          next[cellKeyFor(rowType as any, id, prop)] = 'rejected'
        }
      }
      return next
    })

    setProposedByKey({})
  }

  const runBulkAi = async () => {
    if (!editMode || bulkAiRunning) return

    const props = ['tooltip', 'formDescription', 'aiContext', 'pdfDescription', 'pdfDecisionSummary'] as const

    // Build list of cells that have no value yet (base or override), skipping already-proposed cells
    const todo: Array<{
      rowType: 'section' | 'group' | 'field'
      id: string
      prop: typeof props[number]
      currentValue: string
    }> = []

    const fieldOnlyProps = new Set(['pdfDescription', 'pdfDecisionSummary'])

    for (const section of visibleSections) {
      for (const prop of props) {
        if (fieldOnlyProps.has(prop)) continue
        const base = (section as any)[prop] ?? ''
        const effective = getEffectiveValue('section', section.id, prop, base)
        if (!isFilled(effective) && !getProposed('section', section.id, prop)) {
          todo.push({ rowType: 'section', id: section.id, prop, currentValue: effective })
        }
      }
      for (const group of (section.groups || [])) {
        for (const prop of props) {
          if (fieldOnlyProps.has(prop)) continue
          const base = (group as any)[prop] ?? ''
          const effective = getEffectiveValue('group', group.id, prop, base)
          if (!isFilled(effective) && !getProposed('group', group.id, prop)) {
            todo.push({ rowType: 'group', id: group.id, prop, currentValue: effective })
          }
        }
      }
      for (const field of section.fields) {
        for (const prop of props) {
          const base = (field as any)[prop] ?? ''
          const effective = getEffectiveValue('field', field.id, prop, base)
          if (!isFilled(effective) && !getProposed('field', field.id, prop)) {
            todo.push({ rowType: 'field', id: field.id, prop, currentValue: effective })
          }
        }
      }
    }

    if (todo.length === 0) return

    setBulkAiRunning(true)
    setBulkAiProgress({ done: 0, total: todo.length })
    bulkAiCancelledRef.current = false

    for (let i = 0; i < todo.length; i++) {
      if (bulkAiCancelledRef.current) break
      await askAiForCell(todo[i])
      setBulkAiProgress({ done: i + 1, total: todo.length })
    }

    setBulkAiRunning(false)
    setBulkAiProgress(null)
  }

  const rejectCell = (rowType: 'section' | 'group' | 'field', id: string, prop: string) => {
    const key = keyFor(rowType, id)

    setDecisionByCellKey((prev) => ({
      ...prev,
      [cellKeyFor(rowType, id, prop)]: 'rejected',
    }))

    setProposedByKey((prev) => {
      const next = { ...prev }
      const row = { ...(next[key] || {}) }
      delete row[prop]
      if (Object.keys(row).length === 0) {
        delete next[key]
      } else {
        next[key] = row
      }
      return next
    })
  }

  const renderEditableCell = (args: {
    rowType: 'section' | 'group' | 'field'
    id: string
    prop: 'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary'
    baseValue: string
  }) => {
    const cellKey = cellKeyFor(args.rowType, args.id, args.prop)
    const override = getOverride(args.rowType, args.id, args.prop)
    const effectiveBase = override !== undefined ? override : args.baseValue
    const proposed = getProposed(args.rowType, args.id, args.prop)
    const hasProposal = proposed !== undefined && proposed !== effectiveBase

    const aiLoading = !!aiLoadingByCellKey[cellKey]
    const aiError = aiErrorByCellKey[cellKey] || ''

    const isEditing = editingCellKey === cellKey

    const startEdit = () => {
      if (!editMode) return
      setEditingCellKey(cellKey)
      setEditingValue(effectiveBase || '')
    }

    const cancelEdit = () => {
      setEditingCellKey('')
      setEditingValue('')
    }

    const saveEdit = () => {
      const rowKey = keyFor(args.rowType, args.id)

      setOverridesByKey((prev) => ({
        ...prev,
        [rowKey]: {
          ...(prev[rowKey] || {}),
          [args.prop]: editingValue,
        },
      }))

      setDecisionByCellKey((prev) => ({
        ...prev,
        [cellKey]: 'accepted',
      }))

      setProposedByKey((prev) => {
        const next = { ...prev }
        const row = { ...(next[rowKey] || {}) }
        delete row[args.prop]
        if (Object.keys(row).length === 0) {
          delete next[rowKey]
        } else {
          next[rowKey] = row
        }
        return next
      })

      cancelEdit()
    }

    return (
      <div className="space-y-2">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              className="w-full min-h-24 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded p-2 text-xs text-[var(--text-secondary)]"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={saveEdit}
              >
                Save
              </button>
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="whitespace-pre-wrap">{effectiveBase || ''}</div>
            {editMode ? (
              <div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                    onClick={startEdit}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={aiLoading}
                    className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600 disabled:opacity-50"
                    onClick={() =>
                      askAiForCell({
                        rowType: args.rowType,
                        id: args.id,
                        prop: args.prop,
                        currentValue: effectiveBase || '',
                      })
                    }
                    title="Ask AI to suggest a value for this cell"
                  >
                    {aiLoading ? 'Asking…' : 'Ask AI'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {aiError ? <div className="text-[10px] text-red-300">{aiError}</div> : null}

        {hasProposal ? (
          <div className="border border-green-700/50 bg-green-900/10 rounded p-2">
            <div className="whitespace-pre-wrap text-green-300">{proposed}</div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-green-700/60 text-green-200 hover:border-green-500"
                onClick={() => acceptCell(args.rowType, args.id, args.prop)}
              >
                Accept
              </button>
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-red-700/60 text-red-200 hover:border-red-500"
                onClick={() => rejectCell(args.rowType, args.id, args.prop)}
              >
                Reject
              </button>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  const closePromptModal = () => {
    setPromptModalOpen(false)
    setPromptModalValue('')
  }

  const savePromptModal = () => {
    setAiPromptTemplatesByProp((prev) => ({
      ...prev,
      [promptModalProp]: promptModalValue,
    }))
    closePromptModal()
  }

  const openPromptTemplateModal = (prop: 'formDescription' | 'tooltip' | 'aiContext' | 'pdfDescription' | 'pdfDecisionSummary') => {
    setPromptModalOpen(true)
    setPromptModalProp(prop)
    setPromptModalValue(aiPromptTemplatesByProp[prop] || '')
  }

  const groupsBySectionId = useMemo(() => {
    const map = new Map<string, typeof groups>()
    for (const g of groups) {
      const existing = map.get(g.sectionId) || []
      existing.push(g)
      map.set(g.sectionId, existing)
    }
    return map
  }, [groups])

  const fieldsBySectionId = useMemo(() => {
    const map = new Map<string, typeof fields>()
    for (const f of fields) {
      const existing = map.get(f.sectionId) || []
      existing.push(f)
      map.set(f.sectionId, existing)
    }
    return map
  }, [fields])

  const completeness = useMemo(() => {
    const totalAll = visibleSections.length + groups.length + fields.length
    const totalFields = fields.length

    const filledFormDescription =
      visibleSections.filter((s) => isFilled(getOverride('section', s.id, 'formDescription') ?? s.formDescription ?? '')).length +
      groups.filter((g) => isFilled(getOverride('group', g.id, 'formDescription') ?? g.formDescription ?? '')).length +
      fields.filter((f) => isFilled(getOverride('field', f.id, 'formDescription') ?? f.formDescription ?? '')).length

    const filledTooltip =
      visibleSections.filter((s) => isFilled(getOverride('section', s.id, 'tooltip') ?? s.tooltip ?? '')).length +
      groups.filter((g) => isFilled(getOverride('group', g.id, 'tooltip') ?? g.tooltip ?? '')).length +
      fields.filter((f) => isFilled(getOverride('field', f.id, 'tooltip') ?? f.tooltip ?? '')).length

    const filledAiContext =
      visibleSections.filter((s) => isFilled(getOverride('section', s.id, 'aiContext') ?? s.aiContext ?? '')).length +
      groups.filter((g) => isFilled(getOverride('group', g.id, 'aiContext') ?? g.aiContext ?? '')).length +
      fields.filter((f) => isFilled(getOverride('field', f.id, 'aiContext') ?? f.aiContext ?? '')).length

    const filledPdfDescription =
      fields.filter((f) => isFilled(getOverride('field', f.id, 'pdfDescription') ?? f.pdfDescription ?? '')).length

    const filledPdfDecisionSummary =
      fields.filter((f) => isFilled(getOverride('field', f.id, 'pdfDecisionSummary') ?? (f as any).pdfDecisionSummary ?? '')).length

    const filledOptions = fields.filter((f) => optionLabelsForField(f).length > 0).length
    const filledPdfLabel = fields.filter((f) => isFilled(f.pdfLabel ?? '')).length
    const filledVisibleWhen = fields.filter((f) => !!f.visibleWhen && JSON.stringify(f.visibleWhen) !== '{}').length

    return {
      formDescriptionPct: toPct(filledFormDescription, totalAll),
      tooltipPct: toPct(filledTooltip, totalAll),
      aiContextPct: toPct(filledAiContext, totalAll),
      pdfDescriptionPct: toPct(filledPdfDescription, totalFields),
      pdfDecisionSummaryPct: toPct(filledPdfDecisionSummary, totalFields),
      optionsPct: toPct(filledOptions, totalFields),
      pdfLabelPct: toPct(filledPdfLabel, totalFields),
      visibleWhenPct: toPct(filledVisibleWhen, totalFields),
    }
  }, [fields, groups, visibleSections, overridesByKey])

  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'general', label: 'General' },
    { id: 'kyc', label: 'KYC' },
    { id: 'aml', label: 'AML' },
    { id: 'biometrics', label: 'ID Verification' },
    { id: 'kyb', label: 'KYB' },
    { id: 'recipes', label: 'Recipes/Workflows' },
    { id: 'rules', label: 'Risk & Rules' },
    { id: 'pdf_mockup', label: 'PDF Mockup' },
  ]

  const handleExportCsv = () => {
    if (activeTab === 'all') return

    const headers = [
      'rowType',
      'tabId',
      'id',
      'sectionId',
      'groupId',
      'path',
      'valueType',
      'formTitle',
      'formLabel',
      'formDescription',
      'tooltip',
      'aiContext',
      'pdfTitle',
      'pdfLabel',
      'pdfDescription',
      'pdfDecisionSummary',
      'placeholder',
      'helperText',
      'optionsJson',
      'visibleWhenJson',
    ]

    const rows: Array<Record<string, unknown>> = []

    rows.push({
      rowType: 'GUIDE',
      tabId: activeTab,
      id: '',
      sectionId: '',
      groupId: '',
      path: '',
      valueType: '',
      formTitle: '',
      formLabel: '',
      formDescription:
        'AI guide (Form description): helper text shown under/near the field. Explain what to enter and why. 1–2 sentences, plain language, include constraints/examples if needed. Max ~40 words.',
      tooltip:
        'AI guide (Tooltip): short hover text to clarify a single term or ambiguity. 1 short sentence or phrase. Do not repeat the label. Max ~12 words.',
      aiContext:
        'AI guide (AI context): extra internal context for AI/autofill/rationale (not shown to user). Be specific, include policy/definitions. Max ~80 words.',
      pdfTitle: '',
      pdfLabel: '',
      pdfDescription:
        'AI guide (PDF description): decision-forcing, client-facing text for the PDF that helps stakeholders choose an option. Include: (1) what decision is needed, (2) why it matters / ramifications (risk, compliance, cost, timeline, UX), (3) 2–4 clear options with a recommended default if appropriate, (4) how to decide (who owns it, what inputs are needed). Use plain business language. No UI instructions. Max ~80 words.',
      pdfDecisionSummary:
        'AI guide (PDF decision summary): shown only when a decision HAS been made. Briefly summarise what was decided and any key implications. 1–2 sentences, plain business language. Do NOT list options or ask questions. Max ~40 words.',
      placeholder: '',
      helperText: '',
      optionsJson: '',
      visibleWhenJson: '',
    })

    for (const s of visibleSections) {
      const overrideKey = keyFor('section', s.id)
      rows.push({
        rowType: 'section',
        tabId: s.tabId,
        id: s.id,
        sectionId: s.id,
        groupId: '',
        path: '',
        valueType: '',
        formTitle: s.formTitle,
        formLabel: '',
        formDescription: overridesByKey[overrideKey]?.formDescription ?? s.formDescription ?? '',
        tooltip: overridesByKey[overrideKey]?.tooltip ?? s.tooltip ?? '',
        aiContext: overridesByKey[overrideKey]?.aiContext ?? s.aiContext ?? '',
        pdfTitle: s.pdfTitle,
        pdfLabel: '',
        pdfDescription: overridesByKey[overrideKey]?.pdfDescription ?? s.pdfDescription ?? '',
        pdfDecisionSummary: overridesByKey[overrideKey]?.pdfDecisionSummary ?? (s as any).pdfDecisionSummary ?? '',
        placeholder: '',
        helperText: '',
        optionsJson: '',
        visibleWhenJson: '',
      })
    }

    for (const g of groups) {
      const overrideKey = keyFor('group', g.id)
      rows.push({
        rowType: 'group',
        tabId: g.tabId,
        id: g.id,
        sectionId: g.sectionId,
        groupId: g.id,
        path: '',
        valueType: '',
        formTitle: g.formTitle,
        formLabel: '',
        formDescription: overridesByKey[overrideKey]?.formDescription ?? g.formDescription ?? '',
        tooltip: overridesByKey[overrideKey]?.tooltip ?? g.tooltip ?? '',
        aiContext: overridesByKey[overrideKey]?.aiContext ?? g.aiContext ?? '',
        pdfTitle: g.pdfTitle,
        pdfLabel: '',
        pdfDescription: overridesByKey[overrideKey]?.pdfDescription ?? g.pdfDescription ?? '',
        pdfDecisionSummary: overridesByKey[overrideKey]?.pdfDecisionSummary ?? (g as any).pdfDecisionSummary ?? '',
        placeholder: '',
        helperText: (g as any).helperText || '',
        optionsJson: '',
        visibleWhenJson: (g as any).visibleWhen ? JSON.stringify((g as any).visibleWhen) : '',
      })
    }

    for (const f of fields) {
      const overrideKey = keyFor('field', f.id)
      rows.push({
        rowType: 'field',
        tabId: f.tabId,
        id: f.id,
        sectionId: f.sectionId,
        groupId: (f as any).groupId || '',
        path: f.path,
        valueType: f.valueType,
        formTitle: '',
        formLabel: f.formLabel,
        formDescription: overridesByKey[overrideKey]?.formDescription ?? f.formDescription ?? '',
        tooltip: overridesByKey[overrideKey]?.tooltip ?? f.tooltip ?? '',
        aiContext: overridesByKey[overrideKey]?.aiContext ?? f.aiContext ?? '',
        pdfTitle: '',
        pdfLabel: f.pdfLabel,
        pdfDescription: overridesByKey[overrideKey]?.pdfDescription ?? f.pdfDescription ?? '',
        pdfDecisionSummary: overridesByKey[overrideKey]?.pdfDecisionSummary ?? (f as any).pdfDecisionSummary ?? '',
        placeholder: (f as any).placeholder || '',
        helperText: '',
        optionsJson: f.options ? JSON.stringify(f.options) : '',
        visibleWhenJson: f.visibleWhen ? JSON.stringify(f.visibleWhen) : '',
      })
    }

    const csv = toCsv(rows, headers)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `scope_schema_${activeTab}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportCsvFile = async (file: File) => {
    try {
      const text = await file.text()
      const parsed = parseCsv(text)
      if (parsed.headers.length === 0) {
        setImportError('CSV appears to be empty.')
        return
      }

      const required = ['rowType', 'tabId', 'id']
      const missing = required.filter((h) => !parsed.headers.includes(h))
      if (missing.length > 0) {
        setImportError(`CSV missing required header(s): ${missing.join(', ')}`)
        return
      }

      const nextProposals: Record<string, Record<string, string>> = {}
      let proposalCount = 0

      const getEditable = (row: Record<string, string>) => {
        return {
          formDescription: row.formDescription ?? '',
          tooltip: row.tooltip ?? '',
          aiContext: row.aiContext ?? '',
          pdfDescription: row.pdfDescription ?? '',
          pdfDecisionSummary: row.pdfDecisionSummary ?? '',
        }
      }

      for (const row of parsed.rows) {
        const tabId = row.tabId
        if (activeTab !== 'all' && tabId !== activeTab) continue

        const rowType = row.rowType
        const id = row.id
        if (!rowType || !id) continue

        if (rowType === 'section') {
          const s = sectionById.get(id)
          if (!s) continue
          const next = getEditable(row)
          const set: Record<string, string> = {}
          const currentKey = keyFor('section', id)
          const currentFormDescription = overridesByKey[currentKey]?.formDescription ?? s.formDescription ?? ''
          const currentTooltip = overridesByKey[currentKey]?.tooltip ?? s.tooltip ?? ''
          const currentAiContext = overridesByKey[currentKey]?.aiContext ?? s.aiContext ?? ''
          const currentPdfDescription = overridesByKey[currentKey]?.pdfDescription ?? s.pdfDescription ?? ''
          const currentPdfDecisionSummary = overridesByKey[currentKey]?.pdfDecisionSummary ?? (s as any).pdfDecisionSummary ?? ''

          if (currentFormDescription !== next.formDescription) set.formDescription = next.formDescription
          if (currentTooltip !== next.tooltip) set.tooltip = next.tooltip
          if (currentAiContext !== next.aiContext) set.aiContext = next.aiContext
          if (currentPdfDescription !== next.pdfDescription) set.pdfDescription = next.pdfDescription
          if (currentPdfDecisionSummary !== next.pdfDecisionSummary) set.pdfDecisionSummary = next.pdfDecisionSummary

          const keys = Object.keys(set)
          if (keys.length) {
            nextProposals[currentKey] = set
            proposalCount += keys.length
          }
          continue
        }

        if (rowType === 'group') {
          const g = groupById.get(id)
          if (!g) continue
          const next = getEditable(row)
          const set: Record<string, string> = {}
          const currentKey = keyFor('group', id)
          const currentFormDescription = overridesByKey[currentKey]?.formDescription ?? g.formDescription ?? ''
          const currentTooltip = overridesByKey[currentKey]?.tooltip ?? g.tooltip ?? ''
          const currentAiContext = overridesByKey[currentKey]?.aiContext ?? g.aiContext ?? ''
          const currentPdfDescription = overridesByKey[currentKey]?.pdfDescription ?? g.pdfDescription ?? ''
          const currentPdfDecisionSummary = overridesByKey[currentKey]?.pdfDecisionSummary ?? (g as any).pdfDecisionSummary ?? ''

          if (currentFormDescription !== next.formDescription) set.formDescription = next.formDescription
          if (currentTooltip !== next.tooltip) set.tooltip = next.tooltip
          if (currentAiContext !== next.aiContext) set.aiContext = next.aiContext
          if (currentPdfDescription !== next.pdfDescription) set.pdfDescription = next.pdfDescription
          if (currentPdfDecisionSummary !== next.pdfDecisionSummary) set.pdfDecisionSummary = next.pdfDecisionSummary

          const keys = Object.keys(set)
          if (keys.length) {
            nextProposals[currentKey] = set
            proposalCount += keys.length
          }
          continue
        }

        if (rowType === 'field') {
          const f = fieldById.get(id)
          if (!f) continue
          const next = getEditable(row)
          const set: Record<string, string> = {}
          const currentKey = keyFor('field', id)
          const currentFormDescription = overridesByKey[currentKey]?.formDescription ?? f.formDescription ?? ''
          const currentTooltip = overridesByKey[currentKey]?.tooltip ?? f.tooltip ?? ''
          const currentAiContext = overridesByKey[currentKey]?.aiContext ?? f.aiContext ?? ''
          const currentPdfDescription = overridesByKey[currentKey]?.pdfDescription ?? f.pdfDescription ?? ''
          const currentPdfDecisionSummary = overridesByKey[currentKey]?.pdfDecisionSummary ?? (f as any).pdfDecisionSummary ?? ''

          if (currentFormDescription !== next.formDescription) set.formDescription = next.formDescription
          if (currentTooltip !== next.tooltip) set.tooltip = next.tooltip
          if (currentAiContext !== next.aiContext) set.aiContext = next.aiContext
          if (currentPdfDescription !== next.pdfDescription) set.pdfDescription = next.pdfDescription
          if (currentPdfDecisionSummary !== next.pdfDecisionSummary) set.pdfDecisionSummary = next.pdfDecisionSummary

          const keys = Object.keys(set)
          if (keys.length) {
            nextProposals[currentKey] = set
            proposalCount += keys.length
          }
          continue
        }
      }

      setProposedByKey(nextProposals)
      setDecisionByCellKey({})
      setImportError('')

      if (proposalCount === 0) {
        setImportError('No changes detected in imported CSV (compared to current values).')
      }
    } catch (e: any) {
      setImportError(e?.message || 'Failed to import CSV.')
    }
  }

  const pendingCount = useMemo(
    () => Object.values(proposedByKey).reduce((acc, v) => acc + Object.keys(v).length, 0),
    [proposedByKey]
  )
  const acceptedCount = useMemo(
    () => Object.values(decisionByCellKey).filter((v) => v === 'accepted').length,
    [decisionByCellKey]
  )
  const rejectedCount = useMemo(
    () => Object.values(decisionByCellKey).filter((v) => v === 'rejected').length,
    [decisionByCellKey]
  )

  return (
    <div className="p-6">
      {promptModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl bg-[var(--bg-primary)] border border-[var(--border-color)] rounded shadow-lg p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-[var(--text-primary)]">
                AI prompt template ({promptModalProp})
              </div>
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={closePromptModal}
              >
                Close
              </button>
            </div>

            <textarea
              className="mt-3 w-full min-h-64 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded p-2 text-xs text-[var(--text-secondary)]"
              value={promptModalValue}
              onChange={(e) => setPromptModalValue(e.target.value)}
            />

            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                  onClick={savePromptModal}
                >
                  Save
                </button>
              </div>
              <button
                type="button"
                className="px-2 py-1 text-[10px] font-semibold rounded border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={closePromptModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <div className="text-sm font-semibold text-[var(--text-primary)]">Scope schema</div>
        <div className="text-xs text-[var(--text-muted)] mt-1">
          Sections: {visibleSections.length} · Groups: {groups.length} · Fields: {fields.length}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {canEditSchemaOverrides ? (
            <button
              type="button"
              className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
              onClick={() => setEditMode((v) => !v)}
            >
              {editMode ? 'Editing: On' : 'Editing: Off'}
            </button>
          ) : (
            <span className="px-3 py-2 text-xs text-[var(--text-muted)] italic">
              Read-only — schema edits are restricted.
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
            onClick={() => openPromptTemplateModal('formDescription')}
          >
            AI prompt: Form description
          </button>
          <button
            type="button"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
            onClick={() => openPromptTemplateModal('tooltip')}
          >
            AI prompt: Tooltip
          </button>
          <button
            type="button"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
            onClick={() => openPromptTemplateModal('aiContext')}
          >
            AI prompt: AI context
          </button>
          <button
            type="button"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
            onClick={() => openPromptTemplateModal('pdfDescription')}
          >
            AI prompt: PDF description
          </button>
          <button
            type="button"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
            onClick={() => openPromptTemplateModal('pdfDecisionSummary')}
          >
            AI prompt: PDF decision summary
          </button>
        </div>
      </div>

      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg mt-6">
        <div className="border-b border-[var(--border-color)] overflow-x-auto">
          <div className="flex">
            {TABS.map((t) => {
              const isActive = activeTab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-gray-600'
                  }`}
                  onClick={() => {
                    setActiveTab(t.id)
                    setImportError('')
                    setProposedByKey({})
                    setDecisionByCellKey({})
                    setEditingCellKey('')
                    setEditingValue('')
                  }}
                >
                  {t.label}
                </button>
              )
            })}
          </div>
        </div>

        {activeTab !== 'all' ? (
          <div className="p-4 border-t border-[var(--border-color)] flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={handleExportCsv}
              >
                Export {TABS.find((t) => t.id === activeTab)?.label || activeTab} to CSV
              </button>

              <label className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600 cursor-pointer">
                Import CSV (review)
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    setImportError('')
                    void handleImportCsvFile(f)
                    e.currentTarget.value = ''
                  }}
                />
              </label>

              {/* Bulk AI suggest */}
              {bulkAiRunning ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-300">
                    {bulkAiProgress ? `${bulkAiProgress.done} / ${bulkAiProgress.total}` : 'Starting…'}
                  </span>
                  <button
                    type="button"
                    className="px-3 py-2 text-xs font-semibold rounded-lg border border-red-700/60 text-red-300 hover:border-red-500"
                    onClick={() => { bulkAiCancelledRef.current = true }}
                  >
                    Stop
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-purple-700/60 text-purple-200 hover:border-purple-500 disabled:opacity-50"
                  disabled={!editMode}
                  onClick={runBulkAi}
                  title="Ask AI to suggest values for all empty fields"
                >
                  ✦ Fill empty with AI
                </button>
              )}

              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-green-700/60 text-green-200 hover:border-green-500 disabled:opacity-50 disabled:hover:border-green-700/60"
                disabled={pendingCount === 0}
                onClick={acceptAllProposals}
              >
                Accept all
              </button>

              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-red-700/60 text-red-200 hover:border-red-500 disabled:opacity-50 disabled:hover:border-red-700/60"
                disabled={pendingCount === 0}
                onClick={rejectAllProposals}
              >
                Reject all
              </button>

              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:border-gray-600"
                onClick={() => {
                  setImportError('')
                  setProposedByKey({})
                  setOverridesByKey({})
                  setDecisionByCellKey({})
                  setEditingCellKey('')
                  setEditingValue('')
                  void clearAllSchemaOverrides()
                }}
              >
                Clear
              </button>
            </div>

            {importError ? <div className="text-xs text-red-400 whitespace-pre-wrap">{importError}</div> : null}

            {pendingCount || acceptedCount || rejectedCount ? (
              <div className="text-xs text-[var(--text-muted)]">
                Pending: {pendingCount} · Accepted: {acceptedCount} · Rejected: {rejectedCount}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {activeTab === 'pdf_mockup' ? (
        <Suspense fallback={<div className="text-center py-8 text-sm text-[var(--text-muted)]">Loading...</div>}>
          <PdfMockupPanel overridesByKey={overridesByKey} setOverridesByKey={setOverridesByKey} canEdit={canEditSchemaOverrides} />
        </Suspense>
      ) : activeTab === 'all' ? (
        <>
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 mt-6">
            <div className="text-sm font-semibold text-[var(--text-primary)] mb-3">Sections</div>
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-20rem)]">
              <table className="w-full min-w-max text-xs">
                <thead className="sticky top-0 bg-[var(--bg-secondary)]">
                  <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                    <th className="py-2 pr-3 font-medium">ID</th>
                    <th className="py-2 pr-3 font-medium">Tab</th>
                    <th className="py-2 pr-3 font-medium">Form title</th>
                    <th className="py-2 pr-3 font-medium">Form description</th>
                    <th className="py-2 pr-3 font-medium">AI context</th>
                    <th className="py-2 pr-3 font-medium">PDF title</th>
                    <th className="py-2 pr-3 font-medium">PDF description</th>
                  </tr>
                </thead>
                <tbody className="text-[var(--text-secondary)]">
                  {visibleSections.map((s) => (
                    <tr key={s.id} className="border-b border-[var(--border-color)] align-top">
                      <td className="py-2 pr-3 font-mono text-[var(--text-primary)] whitespace-nowrap">{s.id}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{s.tabId}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{s.formTitle}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{s.formDescription || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{s.aiContext || ''}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{s.pdfTitle}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{s.pdfDescription || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 mt-6">
            <div className="text-sm font-semibold text-[var(--text-primary)] mb-3">Groups</div>
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-20rem)]">
              <table className="w-full min-w-max text-xs">
                <thead className="sticky top-0 bg-[var(--bg-secondary)]">
                  <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                    <th className="py-2 pr-3 font-medium">ID</th>
                    <th className="py-2 pr-3 font-medium">Tab</th>
                    <th className="py-2 pr-3 font-medium">Section</th>
                    <th className="py-2 pr-3 font-medium">Form title</th>
                    <th className="py-2 pr-3 font-medium">Form description</th>
                    <th className="py-2 pr-3 font-medium">Tooltip</th>
                    <th className="py-2 pr-3 font-medium">AI context</th>
                    <th className="py-2 pr-3 font-medium">PDF title</th>
                    <th className="py-2 pr-3 font-medium">PDF description</th>
                  </tr>
                </thead>
                <tbody className="text-[var(--text-secondary)]">
                  {groups.map((g) => (
                    <tr key={g.id} className="border-b border-[var(--border-color)] align-top">
                      <td className="py-2 pr-3 font-mono text-[var(--text-primary)] whitespace-nowrap">{g.id}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{g.tabId}</td>
                      <td className="py-2 pr-3 font-mono whitespace-nowrap">{g.sectionId}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{g.formTitle}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{g.formDescription || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{g.tooltip || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{g.aiContext || ''}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{g.pdfTitle}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{g.pdfDescription || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 mt-6">
            <div className="text-sm font-semibold text-[var(--text-primary)] mb-3">Fields</div>
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-20rem)]">
              <table className="w-full min-w-max text-xs">
                <thead className="sticky top-0 bg-[var(--bg-secondary)]">
                  <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                    <th className="py-2 pr-3 font-medium">Tab</th>
                    <th className="py-2 pr-3 font-medium">Form label</th>
                    <th className="py-2 pr-3 font-medium">Form description</th>
                    <th className="py-2 pr-3 font-medium">Tooltip</th>
                    <th className="py-2 pr-3 font-medium">AI context</th>
                    <th className="py-2 pr-3 font-medium">PDF label</th>
                    <th className="py-2 pr-3 font-medium">PDF description</th>
                    <th className="py-2 pr-3 font-medium">Options</th>
                    <th className="py-2 pr-3 font-medium">Visible when</th>
                    <th className="py-2 pr-3 font-medium">ID</th>
                    <th className="py-2 pr-3 font-medium">Section</th>
                    <th className="py-2 pr-3 font-medium">Path</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                  </tr>
                </thead>
                <tbody className="text-[var(--text-secondary)]">
                  {fields.map((f) => (
                    <tr key={f.id} className="border-b border-[var(--border-color)] align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">{f.tabId}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{f.formLabel}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{f.formDescription || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{f.tooltip || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{f.aiContext || ''}</td>
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{f.pdfLabel}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{f.pdfDescription || ''}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{optionLabelsForField(f).join(', ')}</td>
                      <td className="py-2 pr-3 whitespace-pre-wrap">{f.visibleWhen ? JSON.stringify(f.visibleWhen) : ''}</td>
                      <td className="py-2 pr-3 font-mono text-[var(--text-primary)] whitespace-nowrap">{f.id}</td>
                      <td className="py-2 pr-3 font-mono whitespace-nowrap">{f.sectionId}</td>
                      <td className="py-2 pr-3 font-mono whitespace-nowrap">{f.path}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{f.valueType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 mt-6">
          <div className="text-sm font-semibold text-[var(--text-primary)] mb-3">Fields</div>
          <div className="overflow-x-auto overflow-y-auto h-[calc(100vh-24rem)] min-h-[200px]">
            <table className="w-full min-w-max text-xs">
              <thead className="sticky top-0 bg-[var(--bg-secondary)]">
                <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)]">
                  <th className="py-2 pr-3 font-medium sticky left-0 z-30 bg-[var(--bg-secondary)] w-64 max-w-64">Form label</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium w-[420px] max-w-[420px]">
                    <div className="flex items-center gap-2">
                      <span>Form description ({completeness.formDescriptionPct}%)</span>
                      <span
                        className="px-2 py-0.5 rounded border border-[var(--border-color)] text-[10px] font-semibold text-[var(--text-muted)]"
                        title="AI guide: Form description is helper text shown under/near the field to explain what to enter and why. Use 1–2 sentences, plain language, include constraints/examples if needed. Max ~40 words. Different from tooltip: tooltip is ultra-short and only clarifies a term."
                      >
                        Guide
                      </span>
                    </div>
                  </th>
                  <th className="py-2 pr-3 font-medium w-[360px] max-w-[360px]">
                    <div className="flex items-center gap-2">
                      <span>Tooltip ({completeness.tooltipPct}%)</span>
                      <span
                        className="px-2 py-0.5 rounded border border-[var(--border-color)] text-[10px] font-semibold text-[var(--text-muted)]"
                        title="AI guide: Tooltip is short hover text to clarify a single term or ambiguity. Prefer 1 short sentence or phrase. No repetition of the label. Max ~12 words. Different from form description: description can include context/instructions/examples."
                      >
                        Guide
                      </span>
                    </div>
                  </th>
                  <th className="py-2 pr-3 font-medium w-[520px] max-w-[520px]">AI context ({completeness.aiContextPct}%)</th>
                  <th className="py-2 pr-3 font-medium w-64 max-w-64">Options ({completeness.optionsPct}%)</th>
                  <th className="py-2 pr-3 font-medium w-56 max-w-56">PDF label ({completeness.pdfLabelPct}%)</th>
                  <th className="py-2 pr-3 font-medium w-[420px] max-w-[420px]">
                    <div className="flex items-center gap-2">
                      <span>PDF guidance ({completeness.pdfDescriptionPct}%)</span>
                      <span
                        className="px-2 py-0.5 rounded border border-[var(--border-color)] text-[10px] font-semibold text-[var(--text-muted)]"
                        title="Shown in the PDF when no decision has been made yet. Decision-forcing: explain what needs to be decided, options, ramifications. Max ~80 words."
                      >
                        Guide
                      </span>
                    </div>
                  </th>
                  <th className="py-2 pr-3 font-medium w-[360px] max-w-[360px]">
                    <div className="flex items-center gap-2">
                      <span>PDF decision summary ({completeness.pdfDecisionSummaryPct}%)</span>
                      <span
                        className="px-2 py-0.5 rounded border border-[var(--border-color)] text-[10px] font-semibold text-[var(--text-muted)]"
                        title="Shown in the PDF when a decision HAS been made. Briefly summarise what was decided and any key implications. Max ~40 words."
                      >
                        Guide
                      </span>
                    </div>
                  </th>
                  <th className="py-2 pr-3 font-medium w-72 max-w-72">Visible when ({completeness.visibleWhenPct}%)</th>
                  <th className="py-2 pr-3 font-medium w-72 max-w-72">ID</th>
                  <th className="py-2 pr-3 font-medium w-60 max-w-60">Section</th>
                  <th className="py-2 pr-3 font-medium w-80 max-w-80">Path</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="text-[var(--text-secondary)]">
                {visibleSections.flatMap((s) => {
                  const sectionGroups = groupsBySectionId.get(s.id) || []
                  const sectionFields = fieldsBySectionId.get(s.id) || []

                  const rows: React.ReactNode[] = []

                  const groupByIdLocal = new Map(sectionGroups.map((g) => [g.id, g]))
                  const renderedGroupIds = new Set<string>()

                  rows.push(
                    <tr
                      key={`${s.id}__section`}
                      className="border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]/60"
                    >
                      <td className="py-2 pr-3 text-[var(--text-primary)] font-semibold sticky left-0 z-20 bg-[var(--bg-secondary)] w-64 max-w-64 break-words whitespace-pre-wrap">
                        {s.formTitle}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/15 text-primary text-[10px] font-semibold">
                          Section
                        </span>
                      </td>
                      <td className="py-2 pr-3 w-[420px] max-w-[420px] break-words">{renderEditableCell({ rowType: 'section', id: s.id, prop: 'formDescription', baseValue: s.formDescription || '' })}</td>
                      <td className="py-2 pr-3 w-[360px] max-w-[360px] break-words">{renderEditableCell({ rowType: 'section', id: s.id, prop: 'tooltip', baseValue: s.tooltip || '' })}</td>
                      <td className="py-2 pr-3 w-[520px] max-w-[520px] break-words">{renderEditableCell({ rowType: 'section', id: s.id, prop: 'aiContext', baseValue: s.aiContext || '' })}</td>
                      <td className="py-2 pr-3" />
                      <td className="py-2 pr-3 text-[var(--text-primary)]">{s.pdfTitle}</td>
                      <td className="py-2 pr-3 w-[420px] max-w-[420px]" />
                      <td className="py-2 pr-3 w-[360px] max-w-[360px]" />
                      <td className="py-2 pr-3 font-mono text-[var(--text-primary)] w-72 max-w-72 break-words whitespace-pre-wrap">{s.id}</td>
                      <td className="py-2 pr-3 font-mono w-60 max-w-60 break-words whitespace-pre-wrap">{s.id}</td>
                      <td className="py-2 pr-3" />
                      <td className="py-2 pr-3" />
                    </tr>
                  )

                  for (const f of sectionFields) {
                    if (f.groupId && !renderedGroupIds.has(f.groupId)) {
                      const g = groupByIdLocal.get(f.groupId)
                      if (g) {
                        renderedGroupIds.add(f.groupId)
                        rows.push(
                          <tr key={`${g.id}__group`} className="border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]/35">
                            <td className="py-2 pr-3 text-[var(--text-primary)] font-medium sticky left-0 z-20 bg-[var(--bg-secondary)] w-64 max-w-64 break-words whitespace-pre-wrap">
                              {g.formTitle}
                            </td>
                            <td className="py-2 pr-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-primary)] text-[10px] font-semibold border border-[var(--border-color)]">
                                Group
                              </span>
                            </td>
                            <td className="py-2 pr-3 w-[420px] max-w-[420px] break-words">{renderEditableCell({ rowType: 'group', id: g.id, prop: 'formDescription', baseValue: g.formDescription || '' })}</td>
                            <td className="py-2 pr-3 w-[360px] max-w-[360px] break-words">{renderEditableCell({ rowType: 'group', id: g.id, prop: 'tooltip', baseValue: g.tooltip || '' })}</td>
                            <td className="py-2 pr-3 w-[520px] max-w-[520px] break-words">{renderEditableCell({ rowType: 'group', id: g.id, prop: 'aiContext', baseValue: g.aiContext || '' })}</td>
                            <td className="py-2 pr-3" />
                            <td className="py-2 pr-3 text-[var(--text-primary)]">{g.pdfTitle}</td>
                            <td className="py-2 pr-3 w-[420px] max-w-[420px]" />
                            <td className="py-2 pr-3 w-[360px] max-w-[360px]" />
                            <td className="py-2 pr-3" />
                            <td className="py-2 pr-3 font-mono text-[var(--text-primary)] w-72 max-w-72 break-words whitespace-pre-wrap">{g.id}</td>
                            <td className="py-2 pr-3 font-mono w-60 max-w-60 break-words whitespace-pre-wrap">{g.sectionId}</td>
                            <td className="py-2 pr-3" />
                            <td className="py-2 pr-3" />
                          </tr>
                        )
                      }
                    }

                    rows.push(
                      <tr key={f.id} className="border-b border-[var(--border-color)] align-top">
                        <td className="py-2 pr-3 text-[var(--text-primary)] sticky left-0 z-10 bg-[var(--bg-secondary)] w-64 max-w-64 break-words whitespace-pre-wrap">
                          {f.formLabel}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-transparent text-[var(--text-muted)] text-[10px] font-semibold border border-[var(--border-color)]">
                            Field
                          </span>
                        </td>
                        <td className="py-2 pr-3 w-[420px] max-w-[420px] break-words">{renderEditableCell({ rowType: 'field', id: f.id, prop: 'formDescription', baseValue: f.formDescription || '' })}</td>
                        <td className="py-2 pr-3 w-[360px] max-w-[360px] break-words">{renderEditableCell({ rowType: 'field', id: f.id, prop: 'tooltip', baseValue: f.tooltip || '' })}</td>
                        <td className="py-2 pr-3 w-[520px] max-w-[520px] break-words">{renderEditableCell({ rowType: 'field', id: f.id, prop: 'aiContext', baseValue: f.aiContext || '' })}</td>
                        <td className="py-2 pr-3 w-64 max-w-64 break-words whitespace-pre-wrap">{optionLabelsForField(f).join(', ')}</td>
                        <td className="py-2 pr-3 text-[var(--text-primary)] w-56 max-w-56 break-words whitespace-pre-wrap">{f.pdfLabel}</td>
                        <td className="py-2 pr-3 w-[420px] max-w-[420px] break-words">{renderEditableCell({ rowType: 'field', id: f.id, prop: 'pdfDescription', baseValue: f.pdfDescription || '' })}</td>
                        <td className="py-2 pr-3 w-[360px] max-w-[360px] break-words">{renderEditableCell({ rowType: 'field', id: f.id, prop: 'pdfDecisionSummary', baseValue: (f as any).pdfDecisionSummary || '' })}</td>
                        <td className="py-2 pr-3 w-72 max-w-72 break-words whitespace-pre-wrap">{f.visibleWhen ? JSON.stringify(f.visibleWhen) : ''}</td>
                        <td className="py-2 pr-3 font-mono text-[var(--text-primary)] w-72 max-w-72 break-words whitespace-pre-wrap">{f.id}</td>
                        <td className="py-2 pr-3 font-mono w-60 max-w-60 break-words whitespace-pre-wrap">{f.sectionId}</td>
                        <td className="py-2 pr-3 font-mono w-80 max-w-80 break-words whitespace-pre-wrap">{f.path}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{f.valueType}</td>
                      </tr>
                    )
                  }

                  return rows
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
